import { defineStore } from 'pinia'
import { VSF_MHJ_MY_STORE_ID, VSF_MHJ_MY_STORE_NAME } from '~/config/cookie'

export const useShopStore = defineStore('shop', () => {
  const { fetchStoresByIds } = useStores()
  const userstore = useUserStore()
  const searchTerm = ref('')
  const storesList = ref<SFCCStore[]>([])
  const allStoresList = ref<SFCCStore[]>([])
  const storeStock = ref<StockResult>()
  const myStore = ref<SFCCStore>()

  const myStoreNameCookie = useCookie(VSF_MHJ_MY_STORE_NAME)
  const myStoreIdCookie = useCookie(VSF_MHJ_MY_STORE_ID)

  const myStoreName = computed(() => (userstore?.user && userstore.myStoreName) || myStoreNameCookie.value)
  // NOTE: No docs on why but useCookie(VSF_MHJ_MY_STORE_ID) returns a number instead of a string
  const myStoreId = computed(() => ((userstore?.user && userstore.myStoreId) || myStoreIdCookie.value)?.toString())

  // The id the last store lookup answered for, so a saved id whose store no longer exists stops reading as loading.
  const myStoreLookedUpId = ref<string>()
  const myStorePending = computed(
    () => !!myStoreId.value && myStore.value?.id !== myStoreId.value && myStoreLookedUpId.value !== myStoreId.value,
  )

  const myStoreStock = computed(() => {
    return storeStock.value?.stores?.find((item) => item.store === myStoreId.value)
  })

  const findInStoreIds = computed(() =>
    Array.from(new Set([...(myStoreId.value ? [myStoreId.value] : []), ...storesList.value.map((store) => store.id)])),
  )

  // Bumped by every set/clear so a rollback still waiting on SFCC can tell it has been overtaken.
  let storeChange = 0

  /** Resolves false when a logged-in shopper's save failed and the account's store was restored. */
  async function setMyStore(store: SFCCStore) {
    const change = ++storeChange
    myStore.value = store
    myStoreNameCookie.value = store.name
    myStoreIdCookie.value = store.id
    if (userstore?.user && !(await userstore.setMyStore({ id: store.id, name: store.name }))) {
      await restoreAccountStore(change)
      return false
    }
    return true
  }

  /** Resolves false when a logged-in shopper's save failed and the account's store was restored. */
  async function clearMyStore() {
    const change = ++storeChange
    myStore.value = undefined
    myStoreNameCookie.value = null
    myStoreIdCookie.value = null
    // Logged-in shoppers keep c_preferredStore on SFCC, which would otherwise restore the store.
    if (userstore?.user && !(await userstore.clearMyStore())) {
      await restoreAccountStore(change)
      return false
    }
    return true
  }

  // Matches the local store back to the one saved on the account after a failed save.
  async function restoreAccountStore(change: number) {
    if (change !== storeChange) return
    const storeId = userstore.myStoreId
    myStoreIdCookie.value = storeId ?? null
    myStoreNameCookie.value = userstore.myStoreName ?? null
    if (!storeId) {
      myStore.value = undefined
    } else if (myStore.value?.id !== storeId) {
      const accountStore = (await fetchStoresByIds([storeId]))?.[0]
      // A newer choice, or an older save landing late, may have moved the store on meanwhile.
      if (change === storeChange && myStoreId.value === storeId) myStore.value = accountStore
    }
  }

  async function mergeMyStore() {
    if (!userstore.user?.c_preferredStore && myStoreIdCookie.value) {
      // must use cookie values here as the storeId and Storename get wiped dues to login and no replaced preffered store values
      return await userstore.setMyStore({
        id: myStoreIdCookie.value,
        name: myStoreNameCookie.value,
      })
    }
    if (userstore.user?.c_preferredStore) {
      const storesResult = await fetchStoresByIds([myStoreId.value])
      myStore.value = storesResult[0]
    }
  }

  function reset() {
    searchTerm.value = ''
    storesList.value = []
    storeStock.value = undefined
    myStore.value = undefined
    myStoreLookedUpId.value = undefined
    myStoreNameCookie.value = null
    myStoreIdCookie.value = null
  }

  if (import.meta.client) {
    onNuxtReady(() => {
      watch(
        myStoreId,
        async (newStoreId) => {
          if (newStoreId) {
            if (!myStore.value || myStore.value.id !== newStoreId) {
              const store = (await fetchStoresByIds([newStoreId]))?.[0]
              // The store may have changed again while this lookup was in flight.
              if (myStoreId.value === newStoreId) {
                myStore.value = store
                myStoreLookedUpId.value = newStoreId
              }
            }
          } else {
            myStore.value = undefined
          }
        },
        { immediate: true },
      )
    })
  }

  return {
    searchTerm,
    storesList,
    allStoresList,
    storeStock,
    myStore,
    myStoreName,
    myStoreId,
    myStorePending,
    myStoreStock,
    findInStoreIds,
    setMyStore,
    clearMyStore,
    mergeMyStore,
    reset,
  }
})
